import { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { MessageSquareText, Eye, RotateCcw, Save } from 'lucide-react';

// Per-instance notification copy editor (Radly platform). Edits the title/body of
// each system message; empty fields reset to the registry default. Preview renders
// with sample variables. Sending services fall back to defaults until migrated.
export default function NotificationTemplates() {
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({});      // id -> { title, body }
  const [previews, setPreviews] = useState({}); // id -> { title, body }
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/admin/notification-templates');
      setItems(res.data.templates || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const by = {};
    for (const t of items) (by[t.group] = by[t.group] || []).push(t);
    return Object.entries(by);
  }, [items]);

  const valueFor = (t, field) => {
    const e = edits[t.id];
    if (e && e[field] !== undefined) return e[field];
    return field === 'title' ? t.effectiveTitle : t.effectiveBody;
  };
  const setEdit = (id, field, val) =>
    setEdits((s) => ({ ...s, [id]: { ...s[id], [field]: val } }));

  const preview = async (t) => {
    try {
      const res = await api.post('/admin/notification-templates/preview', {
        id: t.id, title: valueFor(t, 'title'), body: valueFor(t, 'body'),
      });
      setPreviews((s) => ({ ...s, [t.id]: { title: res.data.title, body: res.data.body } }));
    } catch (e) {
      setError(e.response?.data?.error || 'Preview failed');
    }
  };

  const save = async (t) => {
    setSavingId(t.id);
    try {
      await api.put('/admin/notification-templates', {
        id: t.id, title: valueFor(t, 'title'), body: valueFor(t, 'body'),
      });
      setEdits((s) => { const n = { ...s }; delete n[t.id]; return n; });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const reset = async (t) => {
    setSavingId(t.id);
    try {
      await api.put('/admin/notification-templates', { id: t.id, title: '', body: '' });
      setEdits((s) => { const n = { ...s }; delete n[t.id]; return n; });
      setPreviews((s) => { const n = { ...s }; delete n[t.id]; return n; });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Reset failed');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <MessageSquareText className="w-6 h-6 text-amber-500" />
        <h1 className="text-2xl font-bold text-white">Notification Messages</h1>
      </div>
      <p className="text-gray-400 mb-6">
        Customize the wording of system messages. Leave a field empty to use the default. Use variables like
        <span className="font-mono text-gray-200"> {'{{firstName}}'}</span> — they fill in when the message is sent.
      </p>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-700 text-red-300 text-sm">{error}</div>
      )}

      <div className="space-y-6">
        {groups.map(([group, list]) => (
          <div key={group} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700 bg-gray-800/60">
              <h2 className="text-sm font-semibold text-gray-200 uppercase tracking-wide">{group}</h2>
            </div>
            <div className="divide-y divide-gray-700/60">
              {list.map((t) => (
                <div key={t.id} className="px-4 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-white font-medium">{t.label}</span>
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">{t.channel}</span>
                    {t.customized && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300">Custom</span>}
                  </div>

                  <label className="block text-xs text-gray-400 mb-1">Title / subject</label>
                  <input
                    value={valueFor(t, 'title')}
                    onChange={(e) => setEdit(t.id, 'title', e.target.value)}
                    className="w-full mb-2 px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                  />
                  <label className="block text-xs text-gray-400 mb-1">Body</label>
                  <textarea
                    value={valueFor(t, 'body')}
                    onChange={(e) => setEdit(t.id, 'body', e.target.value)}
                    rows={2}
                    className="w-full mb-2 px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                  />

                  {t.variables?.length > 0 && (
                    <div className="text-xs text-gray-500 mb-2">
                      Variables: {t.variables.map((v) => <span key={v} className="font-mono text-gray-400 mr-2">{`{{${v}}}`}</span>)}
                    </div>
                  )}

                  {previews[t.id] && (
                    <div className="mb-2 p-3 rounded-lg bg-gray-900 border border-gray-700">
                      <div className="text-xs text-gray-500 mb-1">Preview (sample data)</div>
                      <div className="text-sm text-white font-medium">{previews[t.id].title}</div>
                      <div className="text-sm text-gray-300">{previews[t.id].body}</div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <button onClick={() => preview(t)} className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200">
                      <Eye className="w-4 h-4" /> Preview
                    </button>
                    <button onClick={() => save(t)} disabled={savingId === t.id} className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium disabled:opacity-50">
                      <Save className="w-4 h-4" /> Save
                    </button>
                    {t.customized && (
                      <button onClick={() => reset(t)} disabled={savingId === t.id} className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg text-gray-400 hover:text-gray-200">
                        <RotateCcw className="w-4 h-4" /> Reset
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
